/**
 * parcours-particuliers.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "parcours" rénovation pour les
 * particuliers : bilan énergétique, audit énergétique, rénovation
 * globale, Mon Accompagnateur Rénov' (MAR).
 */

export interface ParcoursStubKeyPoint {
  iconName: string;
  label: string;
  description: string;
}

export interface ParcoursStubStep {
  title: string;
  description: string;
}

export interface ParcoursStubFAQ {
  question: string;
  answer: string;
}

export interface ParcoursStub {
  slug: string;
  iconName: string;
  eyebrow: string;
  title: string;
  shortLabel: string;
  lead: string;
  badge: string;
  badgeLabel: string;
  heroBullets?: string[];
  keyPoints: ParcoursStubKeyPoint[];
  steps: ParcoursStubStep[];
  faq: ParcoursStubFAQ[];
  ctaLabel: string;
  ctaHref: string;
  secondaryCta?: { label: string; href: string };
}

export const PARCOURS_PARTICULIERS: ParcoursStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'bilan-energetique',
    iconName: 'gauge',
    eyebrow: 'Étape 1 du parcours rénovation',
    title: 'Bilan énergétique',
    shortLabel: 'Bilan énergétique',
    lead: "Une première analyse de vos consommations : elle identifie les sources de déperdition (toiture, murs, fenêtres, chauffage) et vous oriente vers les travaux prioritaires. Gratuit.",
    badge: 'Premier pas',
    badgeLabel: 'Avant tout chantier de rénovation',
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Bilan en ligne en 5 minutes',
        description: "Vous décrivez votre logement en quelques clics. Aucun déplacement, aucun relevé technique compliqué.",
      },
      {
        iconName: 'file-search',
        label: "Un outil d'orientation",
        description: "Ce n'est pas un diagnostic réglementaire : c'est une boussole pour préparer vos travaux, gratuite et sans engagement.",
      },
      {
        iconName: 'sparkles',
        label: 'Vous oriente vers les bonnes aides',
        description: "Identifiez les primes auxquelles vous pouvez prétendre et estimez le montant des aides selon votre profil.",
      },
      {
        iconName: 'home',
        label: 'Préalable utile',
        description: "Avant un diagnostic approfondi ou la signature d'un devis : repérez les travaux les plus rentables pour votre logement.",
      },
    ],
    steps: [
      {
        title: 'Vous décrivez votre logement',
        description: "Type de logement, surface, année de construction, énergie de chauffage, isolation actuelle.",
      },
      {
        title: 'Nous identifions les priorités',
        description: "Analyse des déperditions typiques de votre type de bâti et orientation vers les travaux les plus efficaces.",
      },
      {
        title: "Vous obtenez un plan de travaux estimé",
        description: "Avec une estimation des aides disponibles par type de travaux, avant tout engagement.",
      },
    ],
    faq: [
      {
        question: "Quelle différence avec un diagnostic officiel ?",
        answer:
          "Le diagnostic officiel est un document réglementaire obligatoire pour vendre ou louer, réalisé par un professionnel certifié. Le bilan énergétique est une analyse gratuite d'orientation, sans valeur juridique. Les deux sont complémentaires.",
      },
      {
        question: 'Faut-il payer pour un bilan énergétique ?',
        answer:
          "Non, le bilan est gratuit. Il est proposé par les conseillers en rénovation ou les professionnels du secteur. Méfiez-vous des bilans payants sans valeur ajoutée.",
      },
      {
        question: 'Le bilan suffit-il pour obtenir les aides ?',
        answer:
          "Non. Pour bénéficier des aides, vos travaux doivent être réalisés par un professionnel certifié et vous devez déposer un dossier. Pour une rénovation importante, un diagnostic approfondi et un accompagnement sont obligatoires.",
      },
    ],
    ctaLabel: 'Faire mon bilan en ligne',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Explorer les travaux éligibles', href: '/particuliers/travaux' },
    heroBullets: [
      'Gratuit — sans engagement',
      "Outil d'orientation, pas un diagnostic officiel",
      'Sources officielles citées',
    ],
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'audit-energetique',
    iconName: 'file-search',
    eyebrow: 'Diagnostic réglementaire approfondi',
    title: 'Audit énergétique',
    shortLabel: 'Audit énergétique',
    lead: "Une étude approfondie qui chiffre les travaux nécessaires pour améliorer la performance de votre logement. Obligatoire pour vendre un logement énergivore et pour la rénovation d'ampleur avec aides majorées.",
    badge: '500 – 1 000 €',
    badgeLabel: 'Coût moyen, partiellement remboursable',
    keyPoints: [
      {
        iconName: 'file-search',
        label: 'Réalisé par un professionnel agréé',
        description: "Bureau d'études thermiques, architecte ou conseil certifié : un expert indépendant examine votre logement.",
      },
      {
        iconName: 'gauge',
        label: 'Deux scénarios minimum',
        description: "Un scénario par étapes et un scénario complet, chiffrés et hiérarchisés. Vous choisissez en connaissance de cause.",
      },
      {
        iconName: 'piggy-bank',
        label: 'Aide dédiée au diagnostic',
        description: "L'audit peut être financé à hauteur de 300 à 500 € selon vos revenus.",
      },
      {
        iconName: 'sparkles',
        label: 'Obligatoire pour la rénovation d\'ampleur',
        description: "Toute rénovation visant un gain d'au moins 2 classes énergétiques nécessite un audit préalable.",
      },
    ],
    steps: [
      {
        title: 'Visite sur site',
        description: "L'auditeur examine l'enveloppe du bâtiment, les équipements de chauffage et de ventilation, et relève toutes les caractéristiques techniques.",
      },
      {
        title: 'Modélisation thermique',
        description: "Votre logement est modélisé pour évaluer ses déperditions, ses besoins en chauffage et son étiquette énergétique actuelle.",
      },
      {
        title: 'Remise du rapport',
        description: "Vous recevez un rapport détaillé avec plusieurs scénarios de travaux chiffrés, l'étiquette projetée et les aides mobilisables.",
      },
    ],
    faq: [
      {
        question: "Quand l'audit est-il obligatoire ?",
        answer:
          "L'audit est obligatoire pour vendre un logement de classe F ou G (depuis avril 2023), classe E (à partir de 2025), classe D (à partir de 2034). Il est aussi obligatoire pour bénéficier du parcours de rénovation d'ampleur visant au moins 2 sauts de classe.",
      },
      {
        question: "Quelle différence avec le diagnostic énergétique ?",
        answer:
          "Le diagnostic est une photographie : il évalue la performance actuelle de A à G. L'audit est un projet : il propose des scénarios de travaux chiffrés pour améliorer cette performance.",
      },
      {
        question: "Quel est le coût d'un audit ?",
        answer:
          "Entre 500 et 1 000 € selon la complexité du logement et la région. Une aide peut prendre en charge 300 à 500 € selon vos revenus. Pour le parcours accompagné, le coût est généralement inclus dans la mission d'accompagnement.",
      },
    ],
    ctaLabel: 'Chiffrer mes aides audit',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Demander à être rappelé(e)', href: '/contact?source=particulier&besoin=audit' },
    heroBullets: [
      'Réalisé par un professionnel agréé',
      'Partiellement remboursable par les aides à la rénovation',
      'Obligatoire pour le parcours accompagné',
    ],
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'renovation-globale',
    iconName: 'hammer',
    eyebrow: 'Rénovation d\'ampleur accompagnée',
    title: 'Rénovation globale',
    shortLabel: 'Rénovation globale',
    lead: "Un chantier coordonné qui combine isolation, chauffage et ventilation pour gagner au moins 2 classes énergétiques. Obligatoirement accompagné par un expert agréé.",
    badge: '≥ 2 sauts DPE',
    badgeLabel: 'Objectif minimum (G → E, F → D, etc.)',
    keyPoints: [
      {
        iconName: 'hammer',
        label: 'Des travaux coordonnés',
        description: "Isolation complète + chauffage performant + ventilation : tout est pensé ensemble pour un résultat optimal.",
      },
      {
        iconName: 'users',
        label: 'Un accompagnateur dédié',
        description: "Un expert agréé vous suit du diagnostic à la fin du chantier : audit, plan de travaux, conseil financier, suivi.",
      },
      {
        iconName: 'piggy-bank',
        label: 'Aides majorées',
        description: "Les forfaits du parcours accompagné sont nettement plus élevés que les aides par geste isolé, surtout pour les ménages modestes.",
      },
      {
        iconName: 'gauge',
        label: 'Gain énergétique vérifié',
        description: "Un diagnostic avant et après travaux mesure précisément l'amélioration obtenue.",
      },
    ],
    steps: [
      {
        title: 'Audit énergétique initial',
        description: "Un auditeur agréé établit la situation actuelle et propose plusieurs scénarios de travaux.",
      },
      {
        title: 'Choix du scénario et de l\'accompagnateur',
        description: "Vous sélectionnez le bouquet de travaux et l'expert qui vous suivra pendant tout le chantier.",
      },
      {
        title: 'Dépôt du dossier d\'aides',
        description: "L'accompagnateur vous aide à constituer le dossier avec les devis des professionnels retenus.",
      },
      {
        title: 'Réalisation des travaux',
        description: "Les travaux sont exécutés par des professionnels certifiés pour chaque poste, sous la supervision de votre accompagnateur.",
      },
      {
        title: 'Audit final et versement',
        description: "Un diagnostic après travaux vérifie le gain obtenu, ce qui déclenche le versement des aides.",
      },
    ],
    faq: [
      {
        question: "Qu'est-ce qu'une rénovation globale ?",
        answer:
          "Dans le cadre des aides, c'est une rénovation qui permet un gain d'au moins 2 classes énergétiques (ex. passer de F à D). Le gain est mesuré entre le diagnostic initial et le diagnostic final.",
      },
      {
        question: "L'accompagnateur est-il obligatoire ?",
        answer:
          "Oui pour le parcours de rénovation d'ampleur. L'accompagnateur est obligatoire pour bénéficier des aides majorées. Pour des travaux ponctuels (un seul équipement), il reste facultatif.",
      },
      {
        question: "Quel est le coût total d'une rénovation globale ?",
        answer:
          "Très variable selon la surface et l'état du logement, généralement entre 30 000 € et 80 000 € pour une maison. Mais le cumul des aides + prêt à taux zéro + TVA réduite peut couvrir une part importante du coût.",
      },
    ],
    ctaLabel: 'Calculer mes aides rénovation globale',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Trouver un Accompagnateur Rénov\'', href: '/particuliers/mon-accompagnateur-renov' },
    heroBullets: [
      'Gain ≥ 2 classes énergétiques',
      'Aides majorées pour le parcours accompagné',
      'Suivi inclus du diagnostic au versement',
    ],
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'mon-accompagnateur-renov',
    iconName: 'users',
    eyebrow: "Tiers de confiance agréé par l'État",
    title: "Mon Accompagnateur Rénov' (MAR)",
    shortLabel: "Accompagnateur Rénov'",
    lead: "Un opérateur agréé qui vous guide dans votre rénovation d'ampleur, du diagnostic au suivi du chantier. Obligatoire pour bénéficier des aides majorées.",
    badge: 'Obligatoire',
    badgeLabel: 'Pour le parcours accompagné',
    keyPoints: [
      {
        iconName: 'users',
        label: "Agréé par l'État",
        description: "Bureau d'études, architecte, conseil ou association sélectionné selon un référentiel national de compétences.",
      },
      {
        iconName: 'file-signature',
        label: 'Conseil neutre et indépendant',
        description: "L'accompagnateur ne vend pas de travaux : il vous oriente sans conflit d'intérêts. Une garantie d'objectivité.",
      },
      {
        iconName: 'clipboard-check',
        label: 'Mission complète',
        description: "Audit énergétique, plan de financement, choix des professionnels, suivi du chantier, dossier d'aides : tout est pris en charge.",
      },
      {
        iconName: 'piggy-bank',
        label: 'Coût en partie remboursé',
        description: "La mission d'accompagnement est partiellement remboursée par les aides selon vos revenus.",
      },
    ],
    steps: [
      {
        title: 'Identifiez les accompagnateurs près de chez vous',
        description: "Consultez l'annuaire officiel. Plusieurs accompagnateurs peuvent être agréés dans votre département.",
      },
      {
        title: 'Premier contact et devis',
        description: "L'accompagnateur vous propose un devis pour sa mission selon vos besoins.",
      },
      {
        title: "Mission d'accompagnement",
        description: "Votre accompagnateur pilote toutes les étapes : audit, plan de travaux, dossier d'aides, suivi chantier, diagnostic final.",
      },
      {
        title: 'Versement des aides',
        description: "À la fin du chantier, l'accompagnateur fournit les documents nécessaires pour déclencher le versement des aides.",
      },
    ],
    faq: [
      {
        question: "Comment trouver un accompagnateur agréé ?",
        answer:
          "L'annuaire officiel est disponible sur le site national de la rénovation. Vous pouvez aussi vous rapprocher de votre espace conseil local pour une orientation gratuite.",
      },
      {
        question: "Combien coûte un accompagnateur ?",
        answer:
          "Entre 480 € et 2 000 € selon l'ampleur de la mission (diagnostic seul, accompagnement complet, suivi chantier). Une partie est prise en charge par les aides selon vos revenus.",
      },
      {
        question: "L'accompagnateur peut-il réaliser les travaux ?",
        answer:
          "Non. Pour garantir son indépendance, il ne peut pas être le maître d'œuvre ni le réalisateur des travaux. Il vous aide à sélectionner les professionnels certifiés qui interviendront. C'est une garantie de neutralité.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité au parcours accompagné',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Annuaire officiel France Rénov\'', href: 'https://france-renov.gouv.fr/' },
    heroBullets: [
      "Opérateur agréé par l'État — indépendant",
      'Mission partiellement remboursée par les aides',
      'Obligatoire pour le parcours accompagné',
    ],
  },
];

export function getParcoursStub(slug: string): ParcoursStub | undefined {
  return PARCOURS_PARTICULIERS.find((p) => p.slug === slug);
}
